import { Box, Tooltip, useTheme } from '@mui/material'
import isEmpty from 'lodash/isEmpty'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import CustomChip from '@/components/Chip'
import HtmlKonvaElement from '@/components/HtmlKonvaElement'
import { useAppSelector } from '@/hooks/redux'
import OutputCard from '@/pages/stainingStudio/OutputCard'
import {
  findBackgroundByStumpShadeKey,
  getInitials,
} from '@/pages/stainingStudio/utils'
import {
  removeStumpShadeRecipeKey,
  setSelectedStumpShadeKey,
} from '@/store/fullLayering'
import { getTooltipPlacement } from '@/utils/helper'

const lastTooltipMap: Record<string, { x: number; y: number }> = {}

const StumpShadeStainingTooltip = memo(
  ({ input, output, tooltipPlacement }: any) => {
    const dispatch = useDispatch()
    const theme = useTheme()
    const [tooltipOpen, setTooltipOpen] = useState(true)
    const bgColor = findBackgroundByStumpShadeKey(input?.basecolor)

    const [placement, setPlacement] = useState<'right' | 'left'>('right')

    const canvasImage = useAppSelector((state) => state.app.canvasImage)
    const dualCanvasImage = useAppSelector((state) => state.app.dualCanvasImage)

    const prevOutputRef = useRef<any>(null)
    useEffect(() => {
      const currentX = input?.x
      const currentY = input?.y
      const keyName = input?.basecolor

      const placementResult = getTooltipPlacement({
        keyName,
        currentX,
        currentY,
        open: tooltipOpen,
        canvasImageId: canvasImage?.id,
        output,
        prevOutputRef,
        lastTooltipMap,
      })

      if (placementResult) {
        setPlacement(placementResult)
      }
    }, [tooltipOpen, input?.x, input?.y, output, canvasImage?.id])

    const labelPadding = !isEmpty(output) ? '0 8px 0 0px' : '0'

    const handleDeleteStumpShadeStaining = useCallback(
      (stumpShadeKey: string) => {
        if (!dualCanvasImage) return

        dispatch(setSelectedStumpShadeKey(null))
        dispatch(removeStumpShadeRecipeKey({ key: stumpShadeKey }))
      },
      [dispatch, dualCanvasImage],
    )

    return (
      <HtmlKonvaElement x={input.x} y={input.y} placement={tooltipPlacement}>
        <Box position="relative" sx={{ transform: 'translate(-50%, -60%)' }}>
          <Tooltip
            open={tooltipOpen}
            title={
              isEmpty(input?.targetcolor) ? (
                <OutputCard output={output} setTooltipOpen={setTooltipOpen} />
              ) : null
            }
            arrow
            placement={placement}
            slotProps={{
              popper: { disablePortal: true },
              tooltip: {
                sx: {
                  bgcolor: 'rgba(33, 33, 33, 0.80)',
                  color: 'white',
                  padding: '10px',
                },
              },
              arrow: { sx: { color: 'rgba(33, 33, 33, 0.80)' } },
            }}
          >
            <CustomChip
              onClick={() => setTooltipOpen((prev) => !prev)}
              sx={{
                cursor: 'pointer',
                fontSize: '14px',
                color: theme.palette.common.black,
                padding: '4px 8px',
                background: bgColor,
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                lineHeight: '20px',
                '&:hover': {
                  background: bgColor,
                },
                '& .MuiChip-label': {
                  display: 'flex',
                  alignItems: 'center',
                  padding: labelPadding,
                  lineHeight: '20px',
                },
              }}
              label={getInitials(input?.basecolor)}
              {...(!isEmpty(output) && {
                onDelete: () => {
                  handleDeleteStumpShadeStaining(input?.basecolor)
                },
              })}
            />
          </Tooltip>
        </Box>
      </HtmlKonvaElement>
    )
  },
)

export default StumpShadeStainingTooltip
